import { createFileRoute, Link } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip, Cell, PieChart, Pie, Legend, AreaChart, Area } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Download, TrendingUp, TrendingDown, ExternalLink, Search, MapPin, Users, Phone, Globe, Mail, ShieldCheck, BarChart3, AlertCircle, Info } from "lucide-react";
import { useState, useMemo, Component, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { subDays, startOfDay, endOfDay, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// --- HELPERS ---

const formatarPercentual = (valor: number | null | undefined, casas = 1) => {
  const n = typeof valor === 'number' ? valor : parseFloat(String(valor));
  if (isNaN(n) || !isFinite(n)) return "0,0%";
  return `${n.toFixed(casas).replace('.', ',')}%`;
};

const formatarNumero = (valor: number | null | undefined) => {
  const n = typeof valor === 'number' ? valor : parseInt(String(valor));
  if (isNaN(n) || !isFinite(n)) return "0";
  return n.toLocaleString('pt-BR');
};

const calcularDelta = (curr: any, prev: any) => {
  const c = typeof curr === 'number' ? curr : parseFloat(String(curr));
  const p = typeof prev === 'number' ? prev : parseFloat(String(prev));
  
  if (isNaN(p) || p <= 0) return (isNaN(c) || c <= 0) ? "0%" : "+100%";
  if (isNaN(c)) return "0%";

  const d = ((c - p) / p) * 100;
  return `${d > 0 ? '+' : ''}${d.toFixed(1).replace('.', ',')}%`;
};

// --- ERROR BOUNDARY ---

class BlockErrorBoundary extends Component<{ children: ReactNode, title: string }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: ReactNode, title: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override render() {
    if (this.state.hasError) {
      return (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Erro no bloco: {this.props.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600 mb-2">Ocorreu uma falha ao renderizar este componente.</p>
            <pre className="text-xs bg-white p-3 rounded border border-red-100 overflow-auto max-h-32 text-red-500">
              {this.state.error?.message}
            </pre>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

// --- EMPTY STATE ---

const EmptyState = ({ message = "Ainda não há dados neste período", submessage = "Os dados aparecem conforme os lojistas navegam pela lista." }: { message?: string, submessage?: string }) => (
  <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-center p-8 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
    <BarChart3 className="w-10 h-10 text-gray-300 mb-4" />
    <h3 className="font-medium text-gray-900 mb-1">{message}</h3>
    <p className="text-sm text-gray-500 max-w-[250px]">{submessage}</p>
  </div>
);

export const Route = createFileRoute("/admin/relatorios")({
  component: AdminRelatoriosPage,
});

function AdminRelatoriosPage() {
  const [period, setPeriod] = useState("30");

  const dateRange = useMemo(() => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, parseInt(period)));
    
    // For comparison (previous period)
    const prevEnd = subDays(end, parseInt(period));
    const prevStart = subDays(start, parseInt(period));

    return {
      start: start.toISOString(),
      end: end.toISOString(),
      prevStart: prevStart.toISOString(),
      prevEnd: prevEnd.toISOString()
    };
  }, [period]);

  // Fetch Summary Data
  const { data: summary } = useQuery({
    queryKey: ['report-summary', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_report_summary', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      if (error) throw error;
      
      const { data: prevData } = await supabase.rpc('get_report_summary', {
        start_date: dateRange.prevStart,
        end_date: dateRange.prevEnd
      });

      const { views = 0, clicks = 0, conversion_rate = 0, active_users = 0 } = data?.[0] || {};
      const { views: prevViews = 0, clicks: prevClicks = 0, conversion_rate: prevConversion = 0, active_users: prevUsers = 0 } = prevData?.[0] || {};

      return {
        current: { views, clicks, conversion_rate, active_users },
        deltas: {
          views: calcularDelta(Number(views), Number(prevViews)),
          clicks: calcularDelta(Number(clicks), Number(prevClicks)),
          conversion: calcularDelta(Number(conversion_rate), Number(prevConversion)),
          users: calcularDelta(Number(active_users), Number(prevUsers))
        }
      };
    }
  });

  // Fetch Segments Data
  const { data: segments } = useQuery({
    queryKey: ['report-segments', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_report_segments', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      if (error) throw error;
      return data;
    }
  });

  // Fetch Top Suppliers
  const { data: topSuppliers } = useQuery({
    queryKey: ['report-top-suppliers', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_report_top_fornecedores', {
        start_date: dateRange.start,
        end_date: dateRange.end,
        limit_count: 20
      });
      if (error) throw error;
      return data;
    }
  });

  // Fetch Failed Searches
  const { data: failedSearches } = useQuery({
    queryKey: ['report-failed-searches', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_report_failed_searches', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      if (error) throw error;
      return data;
    }
  });

  // Fetch Blog Events (Search, Autocomplete, Suggestions)
  const { data: blogEvents } = useQuery({
    queryKey: ['report-blog-events', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eventos')
        .select('*')
        .in('tipo', ['busca', 'filtro_segmento'])
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      // Group by specific sub-types identified in the term string
      const summary = {
        autocomplete: data.filter(e => e.termo?.startsWith('autocomplete:')).length,
        direta: data.filter(e => e.tipo === 'busca' && !e.termo?.startsWith('autocomplete:')).length,
        sugestao: data.filter(e => e.termo?.startsWith('sugestao_vazia:')).length,
        total: data.length
      };

      // Evolution by category/type
      const evolution = data.reduce((acc: any[], curr) => {
        const date = format(new Date(curr.created_at || ''), 'dd/MM');
        let entry = acc.find(a => a.date === date);
        if (!entry) {
          entry = { date, busca: 0, sugestao: 0 };
          acc.push(entry);
        }
        if (curr.tipo === 'busca') entry.busca++;
        else if (curr.termo?.startsWith('sugestao_vazia:')) entry.sugestao++;
        return acc;
      }, []).reverse();

      // Frequent terms
      const terms = data
        .filter(e => e.tipo === 'busca' && e.termo)
        .reduce((acc: Record<string, number>, curr) => {
          const term = curr.termo!.replace('autocomplete:', '').trim();
          acc[term] = (acc[term] || 0) + 1;
          return acc;
        }, {});

      const frequentTerms = Object.entries(terms)
        .map(([termo, count]) => ({ termo, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      return { data, summary, evolution, frequentTerms };
    }
  });

  // Fetch Geo Data
  const { data: geoData } = useQuery({
    queryKey: ['report-geo', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_report_geo', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      if (error) throw error;
      return data;
    }
  });

  // Fetch Funnel Data
  const { data: funnel } = useQuery({
    queryKey: ['report-funnel', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_report_funnel', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      if (error) throw error;
      return data?.[0] || { criadas: 0, aprovadas: 0, nao_clientes: 0 };
    }
  });

  // Fetch Channels Data
  const { data: channels } = useQuery({
    queryKey: ['report-channels', dateRange],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_report_channels', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      if (error) throw error;
      return data;
    }
  });

  const exportCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map(obj => Object.values(obj).join(",")).join("\n");
    const csvContent = "data:text/csv;charset=utf-8," + headers + "\n" + rows;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Mock drill-down: neste estágio, apenas preparamos o texto conforme solicitado.
  // Em uma implementação real, o clique filtraria as tabelas abaixo ou abriria um modal.

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#0C1A12]">Relatórios de Performance</h2>
          <p className="text-gray-600 text-sm">Acompanhe métricas de acesso e conversão da plataforma.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 dias</SelectItem>
              <SelectItem value="30">Últimos 30 dias</SelectItem>
              <SelectItem value="90">Últimos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => exportCSV(summary?.current ? [summary.current] : [], "resumo_geral")}>
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {(!summary?.current?.views && !summary?.current?.clicks) && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">Nenhum acesso registrado ainda</AlertTitle>
          <AlertDescription className="text-blue-700">
            Este relatório começa a ser preenchido assim que os primeiros lojistas navegarem pela lista de fornecedores.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <BlockErrorBoundary title="Visualizações">
          <MetricCard 
            title="Visualizações" 
            value={formatarNumero(summary?.current?.views)} 
            delta={summary?.deltas?.views || "0%"}
            icon={<Users className="w-4 h-4" />}
          />
        </BlockErrorBoundary>
        <BlockErrorBoundary title="Cliques">
          <MetricCard 
            title="Cliques de Contato" 
            value={formatarNumero(summary?.current?.clicks)} 
            delta={summary?.deltas?.clicks || "0%"}
            icon={<Phone className="w-4 h-4" />}
          />
        </BlockErrorBoundary>
        <BlockErrorBoundary title="Conversão">
          <MetricCard 
            title="Taxa de Conversão" 
            value={formatarPercentual(summary?.current?.conversion_rate)} 
            delta={summary?.deltas?.conversion || "0%"}
            icon={<TrendingUp className="w-4 h-4" />}
          />
        </BlockErrorBoundary>
        <BlockErrorBoundary title="Ativos">
          <MetricCard 
            title="Usuários Ativos" 
            value={formatarNumero(summary?.current?.active_users)} 
            delta={summary?.deltas?.users || "0%"}
            icon={<ShieldCheck className="w-4 h-4" />}
          />
        </BlockErrorBoundary>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <BlockErrorBoundary title="Acessos por Segmento">
          <Card>
            <CardHeader>
              <CardTitle>Acessos por Segmento</CardTitle>
              <CardDescription>Visualizações e cliques por categoria principal</CardDescription>
            </CardHeader>
            <CardContent className="h-[350px]">
              {(!segments || segments.length === 0) ? (
                <EmptyState />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={segments}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="segmento" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                    <RechartsTooltip />
                    <Bar dataKey="views" name="Visualizações" fill="#0C1A12" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="clicks" name="Cliques" fill="#C6F764" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </BlockErrorBoundary>

        <BlockErrorBoundary title="Distribuição Geográfica">
          <Card>
            <CardHeader>
              <CardTitle>Distribuição Geográfica (Polos)</CardTitle>
              <CardDescription>Concentração de acessos por região</CardDescription>
            </CardHeader>
            <CardContent className="h-[350px]">
              {(!geoData || geoData.length === 0) ? (
                <EmptyState />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={geoData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="polo"
                    >
                      {geoData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] as string} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </BlockErrorBoundary>
      </div>

      <BlockErrorBoundary title="Top Fornecedores">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Top Fornecedores</CardTitle>
              <CardDescription>Os 20 fornecedores com maior engajamento no período</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => exportCSV(topSuppliers || [], "top_fornecedores")}>
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/50">
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>Segmento</TableHead>
                    <TableHead className="text-right">Visualizações</TableHead>
                    <TableHead className="text-right">Cliques</TableHead>
                    <TableHead className="text-right">Conversão</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topSuppliers?.map((f: any) => (
                    <TableRow key={f.id}>
                      <TableCell className="font-medium">{f.nome}</TableCell>
                      <TableCell className="text-sm text-gray-500">{f.segmento}</TableCell>
                      <TableCell className="text-right">{formatarNumero(f.views)}</TableCell>
                      <TableCell className="text-right">{formatarNumero(f.clicks)}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        {formatarPercentual(f.conversion_rate)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!topSuppliers || topSuppliers.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-gray-400">
                        <EmptyState />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </BlockErrorBoundary>

      <div className="grid gap-6 lg:grid-cols-2">
        <BlockErrorBoundary title="Buscas sem Resultado">
          <Card>
            <CardHeader>
              <CardTitle>Buscas sem Resultado</CardTitle>
              <CardDescription>Termos que os lojistas buscaram e não encontraram</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Termo</TableHead>
                    <TableHead className="text-right">Frequência</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {failedSearches?.map((s: any) => (
                    <TableRow key={s.termo}>
                      <TableCell className="font-medium">{s.termo}</TableCell>
                      <TableCell className="text-right">{formatarNumero(s.count)}</TableCell>
                    </TableRow>
                  ))}
                  {(!failedSearches || failedSearches.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-6 text-gray-400">
                        <EmptyState message="Nenhuma busca sem resultado registrada" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </BlockErrorBoundary>

        <BlockErrorBoundary title="Eventos do Blog">
          <Card>
            <CardHeader>
              <CardTitle>Engajamento no Blog</CardTitle>
              <CardDescription>Buscas, autocompletar e sugestões de categorias</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 uppercase font-bold">Autocompletar</p>
                  <p className="text-xl font-bold">{blogEvents?.summary.autocomplete || 0}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 uppercase font-bold">Sugestões (Vazio)</p>
                  <p className="text-xl font-bold">{blogEvents?.summary.sugestao || 0}</p>
                </div>
              </div>
              
              <div className="h-[200px] mb-6">
                <p className="text-xs text-gray-500 uppercase font-bold mb-4">Evolução Diária</p>
                {blogEvents?.evolution && blogEvents.evolution.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={blogEvents.evolution}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                      <RechartsTooltip />
                      <Area type="monotone" dataKey="busca" name="Buscas" stroke="#0C1A12" fill="#0C1A12" fillOpacity={0.1} />
                      <Area type="monotone" dataKey="sugestao" name="Sugestões" stroke="#C6F764" fill="#C6F764" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState message="Sem dados de evolução" />
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold mb-3">Termos mais buscados</p>
                  <div className="rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50/50">
                          <TableHead className="text-xs">Termo</TableHead>
                          <TableHead className="text-right text-xs">Buscas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {blogEvents?.frequentTerms?.map((t: any) => (
                          <TableRow key={t.termo}>
                            <TableCell className="text-sm py-2">{t.termo}</TableCell>
                            <TableCell className="text-right text-sm py-2 font-medium">{t.count}</TableCell>
                          </TableRow>
                        ))}
                        {(!blogEvents?.frequentTerms || blogEvents.frequentTerms.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center py-4 text-gray-400 text-xs">
                              Sem termos frequentes.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase font-bold mb-3">Logs Recentes</p>
                  <div className="rounded-lg border overflow-hidden max-h-[200px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50/50">
                          <TableHead className="text-xs">Evento / Termo</TableHead>
                          <TableHead className="text-right text-xs">Data</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {blogEvents?.data?.slice(0, 10).map((e: any) => (
                          <TableRow key={e.id}>
                            <TableCell className="text-sm py-2">
                              <span className="font-medium text-xs">{e.tipo === 'busca' ? 'Busca: ' : 'Sugestão: '}</span>
                              {e.termo}
                            </TableCell>
                            <TableCell className="text-right text-xs text-gray-400 py-2">
                              {format(new Date(e.created_at), 'dd/MM HH:mm')}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!blogEvents?.data || blogEvents.data.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center py-6 text-gray-400 text-sm">
                              Nenhum evento registrado.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </BlockErrorBoundary>

        <BlockErrorBoundary title="Funil de Canais">
          <Card>
            <CardHeader>
              <CardTitle>Funil de Canais</CardTitle>
              <CardDescription>Origem dos contatos clicados</CardDescription>
            </CardHeader>
            <CardContent className="h-[300px]">
              {(!channels || channels.length === 0) ? (
                <EmptyState />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={channels}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="clicks"
                      nameKey="canal"
                      label={({ name, percent }) => `${name} ${formatarPercentual(Number(percent || 0) * 100, 0)}`}
                    >
                      {channels.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length] as string} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </BlockErrorBoundary>
      </div>
    </div>
  );
}

const COLORS = ["#C6F764", "#0C1A12", "#6B7280", "#E5E7EB", "#1A1A1A"];

function MetricCard({ 
  title, 
  value, 
  delta, 
  icon 
}: { 
  title: string; 
  value: string; 
  delta: string; 
  icon: React.ReactNode 
}) {
  const isPositive = delta.startsWith("+");
  const isNeutral = delta === "0%" || delta === "0.0%" || delta === "0,0%";
  const hasHistory = delta !== "0%" && delta !== "0,0%";
  
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-[#0C1A12]">{value}</div>
        {hasHistory && (
          <div className="flex items-center gap-1 mt-1">
            {isPositive ? (
              <TrendingUp className="w-3 h-3 text-emerald-500" />
            ) : isNeutral ? null : (
              <TrendingDown className="w-3 h-3 text-rose-500" />
            )}
            <span className={`text-xs ${isPositive ? "text-emerald-500" : isNeutral ? "text-muted-foreground" : "text-rose-500"}`}>
              {delta} <span className="text-muted-foreground">vs. período anterior</span>
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
